// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useState, useEffect, useMemo } from 'react';
import * as styles from '../styles/LearningPaths.styles';
import { SkillProfileAssessment } from './SkillProfileView';
import { FeedbackWidget } from './FeedbackWidget';
import { reportAgentInteraction } from '../utils/frontendTracing';

const colors = {
  green: '#00D2AA',
  blue: '#455391',
  textSecondary: '#78716C'
};

const _RECOMMENDED_ACTIONS = [
  {
    type: 'MASTERCLASS',
    title: 'Production Reliability',
    description: 'Focus on zero-downtime indexing and state consistency during high-frequency updates.',
    tag: 'HIGH PRIORITY',
    iconColor: colors.green
  }
  // {
  //   type: 'CASE STUDY',
  //   title: 'Cost Engineering',
  //   description: 'Advanced token management and cache strategy for enterprise LLM deployments.',
  //   tag: 'STRATEGIC',
  //   iconColor: colors.blue
  // },
  // {
  //   type: 'WORKSHOP',
  //   title: 'Technical Storytelling',
  //   description: 'Bridge the gap between complex AI architecture and stakeholder business impact.',
  //   tag: 'LONG-TERM',
  //   iconColor: '#D97706'
  // }
];

const SEQUENCE_LABELS = ['IMMEDIATE FOCUS', 'NEXT MILESTONE', 'FUTURE TARGET'] as const;
const SEQUENCE_COLORS = [colors.green, colors.blue, colors.textSecondary];

const MonitorPlayIcon = ({ color }: { color: string }): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <polygon points="10 8 16 12 10 16 10 8" />
  </svg>
);

const BookIcon = ({ color }: { color: string }): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="16" y2="10" />
  </svg>
);

const PeopleIcon = ({ color }: { color: string }): JSX.Element => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const _ExportIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 17L17 7" />
    <path d="M17 7h-6v6" />
  </svg>
);

const ArrowRightIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const _ClockIcon = (): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ChevronLeftIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

function _getCardIcon(type: string, iconColor: string): JSX.Element {
  switch (type) {
    case 'MASTERCLASS':
      return <MonitorPlayIcon color={iconColor} />;
    case 'CASE STUDY':
      return <BookIcon color={iconColor} />;
    case 'WORKSHOP':
      return <PeopleIcon color={iconColor} />;
    default:
      return <MonitorPlayIcon color={iconColor} />;
  }
}

type Course = { title: string; platform: string; link: string; duration?: number };

type LearningPathResponse = {
  recommendedCourse: Course;
  reasoning: string;
  role: string;
  level: string;
  progressiveCourses?: Course[];
  courseReasonings?: string[];
};

export interface LearningPathsViewProps {
  onBackToSkillProfile?: () => void;
  assessment?: SkillProfileAssessment;
  role?: string;
  level?: string;
  serverCallId?: string;
  userEmail?: string;
}

export const LearningPathsView = ({
  onBackToSkillProfile,
  assessment,
  role,
  level,
  serverCallId,
  userEmail
}: LearningPathsViewProps): JSX.Element => {
  const [learningPath, setLearningPath] = useState<LearningPathResponse | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const sequenceSteps = useMemo(() => {
    const names =
      learningPath?.progressiveCourses && learningPath.progressiveCourses.length >= 3
        ? learningPath.progressiveCourses.slice(0, 3).map((c) => c.title)
        : learningPath?.recommendedCourse
          ? [learningPath.recommendedCourse.title, '—', '—']
          : ['—', '—', '—'];
    return SEQUENCE_LABELS.map((label, i) => ({
      number: String(i + 1).padStart(2, '0'),
      label,
      name: names[i],
      color: SEQUENCE_COLORS[i]
    }));
  }, [learningPath]);

  console.log('LEARNING PATH', learningPath);

  useEffect(() => {
    const fetchLearningPaths = async (): Promise<void> => {
      if (!assessment || !role) {
        setError('Missing required data to fetch learning paths');
        return;
      }

      // Use the assessed level from the AI interview, not the self-selected level
      const assessedLevel = String(assessment.assessedLevel);

      setLoading(true);
      setError(undefined);

      const payload = {
        role,
        level: assessedLevel,
        overallScore: assessment.overallScore || 0,
        keyStrengths: assessment.keyStrengths || [],
        priorityGaps: assessment.conceptGaps || [],
        serverCallId
      };
      const startTime = Date.now();

      try {
        const response = await fetch('/api/learningPaths', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('auth-unauthorized'));
          throw new Error('Unauthorized');
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch learning paths: ${response.statusText}`);
        }

        const result: LearningPathResponse = await response.json();
        setLearningPath(result);
        reportAgentInteraction({
          sessionId: serverCallId,
          callType: 'get-learning-paths',
          request: payload,
          response: result,
          status: 'success',
          startTime,
          endTime: Date.now()
        });
      } catch (error: any) {
        console.error('Error fetching learning paths:', error);
        setError('Could not generate learning paths. Please try again in a moment.');
        reportAgentInteraction({
          sessionId: serverCallId,
          callType: 'get-learning-paths',
          request: payload,
          status: 'error',
          errorMessage: error instanceof Error ? error.message : String(error),
          startTime,
          endTime: Date.now()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLearningPaths();
  }, [assessment, role]);

  if (loading) {
    return (
      <div style={styles.root}>
        <div style={styles.loadingMessage}>Generating your personalized recommendations…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.root}>
        {onBackToSkillProfile && (
          <button
            type="button"
            style={styles.backLink}
            onClick={onBackToSkillProfile}
            aria-label="Back to Skill Profile"
          >
            <ChevronLeftIcon />
            Back to Skill Profile
          </button>
        )}
        <div style={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {onBackToSkillProfile && (
        <button type="button" style={styles.backLink} onClick={onBackToSkillProfile} aria-label="Back to Skill Profile">
          <ChevronLeftIcon />
          Back to Skill Profile
        </button>
      )}

      <div style={styles.sectionHeader}>
        <div style={styles.sectionHeaderLeft}>
          <div style={styles.sectionLabelRow}>
            <span style={styles.sectionLabelLine} />
            <span style={styles.sectionLabelText}>Learning Paths</span>
          </div>
          <h2 style={styles.sectionTitle}>
            Recommended Actions
            {assessment?.assessedLevel ? ` (Level ${assessment.assessedLevel})` : ''}
          </h2>
          {/* <p style={styles.sectionSubtitle}>
            Personalized trainings designed to bridge your identified skill gaps for {role}&nbsp;{level} level.
          </p> */}
        </div>
      </div>

      {learningPath && (
        <>
          {learningPath?.progressiveCourses && learningPath.progressiveCourses.length >= 2 && (
            <div style={{ ...styles.cardsRow, gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {learningPath.progressiveCourses.slice(0, 2).map((course, i) => (
                <div key={course?.link ?? i} style={styles.actionCard}>
                  <div style={styles.featuredHeader}>
                    <div style={styles.featuredBadge}>
                      {course?.platform?.trim() === 'Zaphod' ? 'Internal' : 'External'}
                    </div>
                  </div>
                  <h3 style={styles.cardTitle}>{course?.title ?? ''}</h3>
                  <div style={styles.featuredPlatform}>
                    <BookIcon color={colors.blue} />
                    <span>{course?.platform ?? ''}</span>
                    {typeof course?.duration !== 'undefined' && course.duration !== null && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          marginLeft: 8,
                          fontSize: 14,
                          fontWeight: 600
                        }}
                      >
                        <_ClockIcon />
                        <span>{`${course.duration} Hours`}</span>
                      </span>
                    )}
                  </div>
                  {(learningPath.courseReasonings?.[i] ?? '') !== '' && (
                    <p style={styles.featuredReasoning}>{learningPath.courseReasonings?.[i]}</p>
                  )}
                  <a href={course?.link ?? '#'} target="_blank" rel="noopener noreferrer" style={styles.exploreButton}>
                    Start Course
                    <ArrowRightIcon />
                  </a>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <section style={styles.sequenceSection}>
        <h3 style={styles.sequenceTitle}>Learning Roadmap</h3>
        <div style={styles.sequenceRow}>
          {sequenceSteps.map((step, index) => (
            <div
              key={step.number}
              style={{
                alignItems: 'stretch',
                display: 'flex',
                gap: 12,
                width: '30%',
                minWidth: '30%',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  ...styles.stepCapsule,
                  borderColor: step.color,
                  opacity: index === 2 ? 0.85 : 1
                }}
              >
                <div style={{ ...styles.stepNumber, backgroundColor: index === 2 ? colors.textSecondary : step.color }}>
                  {step.number}
                </div>
                <div>
                  <div style={{ ...styles.stepLabel, color: step.color }}>{step.label}</div>
                  <div style={styles.stepName}>{step.name}</div>
                </div>
              </div>
              {index < sequenceSteps.length - 1 && (
                <span style={styles.arrowConnector}>
                  <ArrowRightIcon />
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {serverCallId && (
        <FeedbackWidget
          feedbackId={serverCallId}
          feedbackType="learning_path"
          onFeedbackSubmitted={(rating) => console.log('Learning path feedback submitted:', rating)}
          userEmail={userEmail}
          assessmentData={learningPath}
          serverCallId={serverCallId}
        />
      )}
    </div>
  );
};
